import React from 'react';
import CapitalActionCard from './CapitalActionCard';
import { CommitmentType, CurrencyCode } from 'further-types/lp/vehicle';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { Alert } from '@mui/material';

interface ActionLinkedCapitalActionsContentProps {
  capitalActions: Array<LinkedCapitalAction>;
  baseCurrency: CurrencyCode;
}

const ActionLinkedCapitalActionsContent: React.FC<
  ActionLinkedCapitalActionsContentProps
> = ({ capitalActions, baseCurrency }) => {
  const { previousSection, nextSection, goToNextSection, goToPreviousSection } =
    useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId, vehicle } = useLayoutContext();

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getLinkedCapitalActions,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        saveActionLinkedCapitalActions: true,
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  return (
    <>
      {vehicle?.commitmentType === CommitmentType.CapitalAndLoan && (
        <Alert severity="info">
          This step will calculate the total gross amount to be called from each
          investor. Where an investor has a capital contribution to be called,
          their capital contribution will be deducted from their total gross
          call amount.
        </Alert>
      )}
      <EqualisationsTableHeader
        headers={['Name', 'Date', 'Amount']}
        gridTemplateColumns="60% 20% 20%"
      />
      {capitalActions?.map((capitalAction, index) => (
        <CapitalActionCard
          key={capitalAction.id}
          capitalAction={capitalAction}
          index={index}
          isLastElement={index === capitalActions.length - 1}
          baseCurrency={baseCurrency}
        />
      ))}
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={false}
        loading={updateEqualisation.isPending}
      />
    </>
  );
};

export default ActionLinkedCapitalActionsContent;
